// FR-RD14 scope item 1: persist {localSessionId, orgId, title, contentType, grackleId,
// partSize, completedParts, finalized, status, updatedAt} after session creation and after
// every accepted part — not just on resume. This is what makes a *live* recording resumable
// if the process dies before `finish()` ever runs: recovery/stateMachine.ts picks up exactly
// this shape next launch. Desktop/mobile write the disk.rs sidecar; web writes IndexedDB.
import { isTauri, writeUploadState } from "../../desktop";
import { putUploadState } from "../persist/chunkStore";
import type { UploadQueueCompletedPart } from "../uploadQueue";
import type { PersistedUploadState } from "./types";

export interface LiveStatePersister {
  onSessionCreated: (grackleId: string, partSize: number) => void;
  onPartAccepted: (part: UploadQueueCompletedPart) => void;
  /** Called once `finish()` has actually succeeded, right before the caller deletes local
   * data — not strictly required (deleteLocal makes the row moot), but keeps the persisted
   * state honest for the brief window between `complete` succeeding and local cleanup. */
  finalize: () => void;
  /** Scope item 4 (mobile/offline publish): called when recording stops while offline, so the
   * persisted state honestly reflects "waiting for a connection" rather than looking like an
   * ordinary in-flight upload. Purely a label — `queue.finish()` is already retrying in the
   * background regardless, and `onSessionCreated`/`onPartAccepted` still flip status back to
   * "uploading" the moment real progress happens. */
  markPendingUpload: (reason: string) => void;
  /** Called on every captured chunk: re-stamps `updatedAt` (throttled) so recovery can tell a
   * session that is still being recorded from one a dead tab left behind — see
   * `LIVE_SESSION_FRESH_MS` in stateMachine.ts. */
  touch: () => void;
}

/** How often `touch()` actually writes. Well under LIVE_SESSION_FRESH_MS. */
const TOUCH_INTERVAL_MS = 10_000;

function persist(input: PersistedUploadState): void {
  // FR-RD14: every live write is also a heartbeat (see LIVE_SESSION_FRESH_MS).
  const state: PersistedUploadState = { ...input, liveHeartbeatAt: Date.now() };
  // Best-effort: a durability layer, not a gate (same posture as disk.rs's own fsync calls —
  // see RecorderPage's handleChunk). A write failure here (e.g. there was no on-disk session
  // to write a sidecar next to) just means this recording can't be auto-resumed, not that
  // recording or uploading should stop.
  if (isTauri()) {
    writeUploadState(state.localSessionId, JSON.stringify(state)).catch(() => {});
  } else {
    putUploadState(state).catch(() => {});
  }
}

export function createLiveStatePersister(initial: PersistedUploadState): LiveStatePersister {
  let state = initial;
  persist(state); // FR-RD14: written before the first chunk, so even a crash with 0 parts
  // accepted still has an orgId/title/contentType to resume into.

  return {
    onSessionCreated(grackleId, partSize) {
      state = { ...state, grackleId, uploadId: grackleId, partSize, status: "uploading", updatedAt: Date.now() };
      persist(state);
    },
    onPartAccepted(part) {
      state = {
        ...state,
        completedParts: [...state.completedParts.filter((p) => p.n !== part.n), part],
        status: "uploading",
        updatedAt: Date.now(),
      };
      persist(state);
    },
    finalize() {
      state = { ...state, finalized: true, updatedAt: Date.now() };
      persist(state);
    },
    touch() {
      const now = Date.now();
      if (now - (state.liveHeartbeatAt ?? state.updatedAt) < TOUCH_INTERVAL_MS) return;
      state = { ...state, updatedAt: now, liveHeartbeatAt: now };
      persist(state);
    },
    markPendingUpload(reason) {
      state = { ...state, status: "pending_upload", lastError: reason, updatedAt: Date.now() };
      persist(state);
    },
  };
}
